/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';

const siteConfig = require('../../siteConfig');

export const siteUrl = siteConfig.url;
export const personId = `${siteUrl}/#person`;
export const websiteId = `${siteUrl}/#website`;

// Who the site is about, for search engines and AI answers. The site shows
// the short name; the markup gives the full one so searches for either match.
// Every other fact here is also stated on the About page.
const person = {
  '@type': 'Person',
  '@id': personId,
  name: siteConfig.fullName,
  alternateName: [siteConfig.name, 'Hassan Ahmed', 'hassan11196'],
  url: `${siteUrl}/`,
  image: `${siteUrl}/muhammad-hassan-ahmed.jpg`,
  description:
    'Inference Platform Engineer at Lyceum in Zurich, working on dedicated inference for open models. Previously a software engineer at CERN on the CMS experiment and a research engineer at Retrocausal.',
  jobTitle: 'Inference Platform Engineer',
  worksFor: {
    '@type': 'Organization',
    name: 'Lyceum',
    url: 'https://lyceum.technology'
  },
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Zurich',
    addressCountry: 'CH'
  },
  alumniOf: [
    {
      '@type': 'CollegeOrUniversity',
      name: 'FAST-NUCES',
      alternateName: 'National University of Computer and Emerging Sciences'
    },
    { '@type': 'Organization', name: 'CERN', url: 'https://home.cern' },
    { '@type': 'Organization', name: 'Retrocausal' }
  ],
  knowsAbout: [
    'Large language models',
    'LLM inference',
    'LLM agents',
    'Retrieval-augmented generation',
    'Machine learning',
    'Computer vision',
    'Kubernetes',
    'Full-stack web development'
  ],
  award: [
    'CMS Award 2024',
    'Runner-up, Argusa AI Challenge 2025 at EPFL'
  ],
  sameAs: [
    siteConfig.linkedin,
    siteConfig.github,
    siteConfig.kaggle,
    siteConfig.cern_gitlab,
    siteConfig.medium,
    siteConfig.orcid,
    siteConfig.googleScholar
  ]
};

const website = {
  '@type': 'WebSite',
  '@id': websiteId,
  url: `${siteUrl}/`,
  name: siteConfig.name,
  alternateName: [siteConfig.fullName, 'Hassan Ahmed', 'hassanahmed.net'],
  inLanguage: 'en',
  author: { '@id': personId },
  publisher: { '@id': personId }
};

// How pages name the author: the Person's @id plus the name and URL Google
// asks for on articles.
export const authorRef = {
  '@type': 'Person',
  '@id': personId,
  name: siteConfig.fullName,
  url: `${siteUrl}/about/`
};

function SEO({ description, lang, meta, keywords, title, titleTemplate, schema }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            image
          }
        }
      }
    `
  );

  const metaDescription = description || site.siteMetadata.description;

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={titleTemplate || `%s | ${site.siteMetadata.title}`}
      meta={[
        {
          name: `description`,
          content: metaDescription
        },
        {
          name: `image`,
          content: site.siteMetadata.image
        },
        {
          property: `og:title`,
          content: title
        },
        {
          property: `og:description`,
          content: metaDescription
        },
        {
          property: `og:type`,
          content: `website`
        },
        {
          property: `og:site_name`,
          content: site.siteMetadata.title
        },
        {
          name: `twitter:card`,
          content: `summary`
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author
        },
        {
          name: `twitter:title`,
          content: title
        },
        {
          name: `twitter:description`,
          content: metaDescription
        }
      ]
        .concat(
          keywords.length > 0
            ? {
                name: `keywords`,
                content: keywords.join(`, `)
              }
            : []
        )
        .concat(meta)}
    >
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': [website, person, ...schema]
        })}
      </script>
    </Helmet>
  );
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  description: ``,
  schema: []
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  titleTemplate: PropTypes.string,
  schema: PropTypes.arrayOf(PropTypes.object)
};

export default SEO;
